import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/utils';

const LOGO = "https://customer-assets.emergentagent.com/job_9b5e32bd-6e03-4600-841f-a63162f850e8/artifacts/w487gkes_lexorbit-logo.png.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const token = params.get('token') || '';

  const [pass, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const inputClass = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#B8B8B8]/40 rounded-lg px-4 py-3 pl-11 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/50 transition-all";

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pass.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return; }
    if (pass !== confirm) { setError('Las contraseñas no coinciden'); return; }
    setLoading(true);
    try {
      await resetPassword(token, pass);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, 'No se pudo restablecer la contraseña. El enlace puede haber vencido.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070707] px-4">
      <div className="w-full max-w-[440px]">
        <div className="bg-[#0a0a0a]/95 backdrop-blur-2xl border border-[rgba(201,164,76,0.18)] rounded-2xl p-8 lg:p-9">
          <div className="text-center mb-7">
            <img src={LOGO} alt="LexOrbit" className="h-14 mx-auto mb-4" />
            <h2 className="text-[26px] text-[#F8F6F0] tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              {done ? 'Contraseña actualizada' : 'Nueva contraseña'}
            </h2>
          </div>

          {!token && !done && (
            <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 mb-4 text-red-400 text-xs">
              Enlace inválido. Volvé a pedir el restablecimiento desde la pantalla de inicio.
            </div>
          )}

          {done ? (
            <div className="text-center space-y-5">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
              <p className="text-[#B8B8B8] text-sm">Tu contraseña se actualizó correctamente. Ya podés iniciar sesión.</p>
              <button onClick={() => navigate('/')}
                className="w-full text-[#050505] font-bold py-3 rounded-lg flex items-center justify-center gap-2 text-sm"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                Ir a iniciar sesión <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3.5">
              {error && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-3 text-red-400 text-xs">{error}</div>}
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                <input type={show ? 'text' : 'password'} placeholder="Nueva contraseña" className={inputClass}
                  value={pass} onChange={e => setPass(e.target.value)} required disabled={!token} />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#B8B8B8]/40 hover:text-[#C9A44C]">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]/40" />
                <input type="password" placeholder="Confirmar contraseña" className={inputClass}
                  value={confirm} onChange={e => setConfirm(e.target.value)} required disabled={!token} />
              </div>
              <button type="submit" disabled={loading || !token}
                className="w-full text-[#050505] font-bold py-3 rounded-lg flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                {loading ? 'Guardando...' : 'Guardar contraseña'}{!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
